import { supabase } from "@/integrations/supabase/client";
import { extractTextFromPdfFile } from "@/lib/pdf/extractPdfText";

export type AttachmentKind = "image" | "pdf" | "doc" | "ppt" | "text" | "audio" | "other";

export interface ProcessedAttachment {
  name: string;
  kind: AttachmentKind;
  size: number;
  /** Extracted text (for documents/audio). */
  text?: string;
  /** Public/signed URL (for images, sent as vision part). */
  imageUrl?: string;
  /** Storage path (so we can persist for reload). */
  storagePath?: string;
  /** Set if processing failed but the file was attached. */
  error?: string;
}

const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_TEXT_CHARS = 30_000; // per file, sent to LLM
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export function getAttachmentKind(file: File): AttachmentKind {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("audio/")) return "audio";
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (
    name.endsWith(".docx") ||
    name.endsWith(".doc") ||
    type.includes("wordprocessingml") ||
    type === "application/msword"
  ) return "doc";
  if (
    name.endsWith(".pptx") ||
    name.endsWith(".ppt") ||
    type.includes("presentationml") ||
    type === "application/vnd.ms-powerpoint"
  ) return "ppt";
  if (
    type.startsWith("text/") ||
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    name.endsWith(".csv") ||
    name.endsWith(".json")
  ) return "text";
  return "other";
}

function truncate(text: string, max = MAX_TEXT_CHARS): string {
  if (text.length <= max) return text;
  return text.slice(0, max) + `\n\n[Truncated: file exceeded ${max} characters]`;
}

async function uploadToStorage(userId: string, file: File): Promise<string> {
  const ext = file.name.split(".").pop() || "bin";
  const path = `${userId}/${Date.now()}-${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from("chat-attachments")
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  return path;
}

async function getSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("chat-attachments")
    .createSignedUrl(path, 60 * 60 * 24 * 7); // 7 days for the LLM call (vision still resolves)
  if (error || !data) throw error || new Error("Failed to sign URL");
  return data.signedUrl;
}

async function transcribeAudio(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file, file.name);
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/transcribe-audio`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session?.access_token || SUPABASE_KEY}`,
      apikey: SUPABASE_KEY,
    },
    body: formData,
  });
  if (!res.ok) throw new Error(`Transcription failed (${res.status})`);
  const data = await res.json();
  return (data.text as string) || "";
}

async function extractDocxText(file: File): Promise<string> {
  const mammoth = await import("mammoth");
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value || "";
}

export async function processAttachment(
  file: File,
  userId: string,
): Promise<ProcessedAttachment> {
  const kind = getAttachmentKind(file);
  const base: ProcessedAttachment = { name: file.name, kind, size: file.size };

  if (file.size > MAX_FILE_BYTES) {
    return { ...base, error: "File exceeds 20MB limit" };
  }

  try {
    if (kind === "image") {
      const path = await uploadToStorage(userId, file);
      const url = await getSignedUrl(path);
      return { ...base, storagePath: path, imageUrl: url };
    }
    if (kind === "audio") {
      const text = await transcribeAudio(file);
      return { ...base, text: truncate(text) };
    }
    if (kind === "pdf") {
      const result = await extractTextFromPdfFile(file);
      if (!result.success) throw new Error(result.error || "PDF extraction failed");
      const joined = result.pages.map((p) => `[Page ${p.pageNumber}]\n${p.text}`).join("\n\n");
      return { ...base, text: truncate(joined) };
    }
    if (kind === "doc") {
      const text = await extractDocxText(file);
      if (!text.trim()) throw new Error("Could not read document text (legacy .doc not supported, please convert to .docx)");
      return { ...base, text: truncate(text) };
    }
    if (kind === "text") {
      const text = await file.text();
      return { ...base, text: truncate(text) };
    }
    if (kind === "ppt") {
      // Skip text extraction for now; pass filename only
      return { ...base, error: "PowerPoint text extraction is not supported yet — file name shared with AI." };
    }
    return { ...base, error: "Unsupported file type" };
  } catch (err: any) {
    console.error("[processAttachment] Failed:", file.name, err);
    return { ...base, error: err?.message || "Failed to process file" };
  }
}

export async function processAttachments(
  files: File[],
  userId: string,
): Promise<ProcessedAttachment[]> {
  return Promise.all(files.map((f) => processAttachment(f, userId)));
}

/**
 * Build a synthetic text block describing all attachments to append
 * to the user's message context for the LLM.
 */
export function buildAttachmentContextBlock(items: ProcessedAttachment[]): string {
  if (!items.length) return "";
  const parts: string[] = ["", "[Attached files]"];
  for (const it of items) {
    parts.push(`--- file: ${it.name} (${it.kind}) ---`);
    if (it.text) parts.push(it.text);
    else if (it.imageUrl) parts.push("(image attached — see vision input)");
    else if (it.error) parts.push(`(could not extract: ${it.error})`);
    else parts.push("(no extractable text)");
  }
  return parts.join("\n");
}
